import React, { type ComponentType, useEffect, useState } from "react";
import { Formik, type FormikHelpers, type FormikProps } from "formik";
import { useSnackbar } from "notistack";
import {
  Button,
  Card,
  CardContent,
  Grid as GridBase,
  Typography,
} from "@mui/material";

import EmailLocations from "./EmailLocations";
import { emailCampaignHelper } from "@/features/campaigns/email/utils/emailCampaignHelper";

type LocationId = string | number;

type EmailLocationData = {
  id: LocationId;
  name?: string;
  [key: string]: unknown;
};

type EmailTemplateData = {
  id: LocationId;
  [key: string]: unknown;
};

type EmailCampaign = {
  locations: EmailLocationData[];
  templates: EmailTemplateData[];
  [key: string]: unknown;
};

type EmailLocationFormValue = Record<string, unknown>;

type EmailJoinCampaignValues = Record<LocationId, EmailLocationFormValue>;

type EmailJoinCampaignFormProps = {
  campaign: EmailCampaign;
  initialValues: EmailJoinCampaignValues;
  onSubmit: (_values: EmailJoinCampaignValues) => void;
};

type EmailLocationsProps = {
  values: EmailJoinCampaignValues;
  setFieldValue: FormikProps<EmailJoinCampaignValues>["setFieldValue"];
  campaign: EmailCampaign;
  setExpanded: React.Dispatch<React.SetStateAction<LocationId[]>>;
  expanded: LocationId[];
  locations: EmailLocationData[];
};

const Grid = GridBase as ComponentType<any>;
const EmailLocationsList = EmailLocations as ComponentType<EmailLocationsProps>;
const FormikForm = Formik as ComponentType<{
  initialValues: EmailJoinCampaignValues;
  enableReinitialize?: boolean;
  onSubmit: (
    _values: EmailJoinCampaignValues,
    _actions: FormikHelpers<EmailJoinCampaignValues>
  ) => void;
  children: (_props: FormikProps<EmailJoinCampaignValues>) => React.ReactNode;
}>;

function EmailJoinCampaignForm({
  campaign,
  initialValues,
  onSubmit,
}: EmailJoinCampaignFormProps) {
  const [expanded, setExpanded] = useState<LocationId[]>([]);

  const { enqueueSnackbar } = useSnackbar();

  useEffect(() => {
    const fistLocId = campaign?.locations?.[0]?.id || 1;
    setExpanded([fistLocId]);
  }, []);

  const handleFormSubmit = (
    values: EmailJoinCampaignValues,
    actions: FormikHelpers<EmailJoinCampaignValues>
  ) => {
    const filledLocs = emailCampaignHelper.getFilledLocations(
      values
    ) as EmailJoinCampaignValues;

    if (!Object.keys(filledLocs)?.length) {
      enqueueSnackbar("Please make the selection!", {
        variant: "error",
        anchorOrigin: { horizontal: "center", vertical: "bottom" },
      });
      actions.setSubmitting(false);
      return;
    }
    if (emailCampaignHelper.hasInvalidateSelection(values)) {
      enqueueSnackbar(
        "Please check there are some invalid/missing selection!",
        { variant: "error" }
      );
      actions.setSubmitting(false);
      return;
    }

    onSubmit(filledLocs);
    actions.setSubmitting(false);
  };

  const renderPage = ({
    values,
    handleSubmit,
    setFieldValue,
    isSubmitting,
  }: FormikProps<EmailJoinCampaignValues>) => {
    const fullyFilledLocs = Object.keys(
      emailCampaignHelper.getFilledLocations(values)
    );
    return (
      <form onSubmit={handleSubmit}>
        <EmailLocationsList
          values={values}
          setFieldValue={setFieldValue}
          campaign={campaign}
          setExpanded={setExpanded}
          expanded={expanded}
          locations={campaign.locations}
        />
        <Card elevation={0} className="mb-5">
          <CardContent>
            <Grid
              container
              spacing={1}
              justifyContent="flex-end"
              alignItems="center"
            >
              <Grid item>
                <Typography variant="caption">
                  Selection made for {fullyFilledLocs?.length || 0} of{" "}
                  {campaign.locations?.length} locations
                </Typography>
              </Grid>
              <Grid item>
                <Button
                  type="submit"
                  variant="contained"
                  color="primary"
                  className="px-5"
                  size="large"
                  disabled={isSubmitting}
                >
                  Continue
                </Button>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      </form>
    );
  };

  return (
    <>
      <FormikForm
        initialValues={initialValues}
        enableReinitialize
        onSubmit={handleFormSubmit}
      >
        {(props) => renderPage(props)}
      </FormikForm>
    </>
  );
}

export default EmailJoinCampaignForm;
